import React, { useState, useEffect, useCallback } from 'react';
import { notesAPI } from '../utils/notesAPI';
import { decryptNote } from '../utils/noteCrypto';
import { formatFileSize } from '../utils/noteCrypto';
import NoteItem from './NoteItem';
import './NoteList.css';

const NoteList = ({ 
  userKeys, 
  serverPublicKey, 
  onNoteSelect, 
  onNoteEdit, 
  onNoteDelete,
  refreshTrigger,
  editingNoteId = null
}) => {
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState(null);

  const pageSize = 20;

  // Fetch notes list
  const fetchNotes = async (page = 1) => {
    if (!userKeys || !serverPublicKey) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await notesAPI.getNotes(page, pageSize, '', userKeys);
      if (response.success) {
        // Check if notes is null or undefined
        const notes = response.data.notes || [];
        // Decrypt note content
        const decryptedNotes = await Promise.all(
          notes.map(async (note) => {
            try {
              const { title, content } = decryptNote(
                note.title_encrypted,
                note.content_encrypted,
                note.note_key_encrypted,
                userKeys.privateKey
              );
              return {
                ...note,
                title,
                content,
                size: new Blob([content]).size // Calculate content size, not title size
              };
            } catch (error) {
              return {
                ...note,
                title: 'Decryption Failed',
                content: 'Unable to decrypt this note',
                size: 0
              };
            }
          })
        );
        
        setNotes(decryptedNotes);
        setPagination(response.data.pagination);
      } else {
        setError(response.error || 'Failed to fetch notes');
      }
    } catch (error) {
      setError('Failed to fetch notes');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    if (!userKeys) return;
    
    try {
      const response = await notesAPI.getNoteStats(userKeys);
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
    }
  };

  // Delete note
  const handleDeleteNote = async (noteId) => {
    if (!userKeys) return;
    
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }
    
    try {
      const response = await notesAPI.deleteNote(noteId, userKeys);
      if (response.success) {
        setNotes(notes.filter(note => note.note_id !== noteId));
        if (onNoteDelete) {
          onNoteDelete(noteId);
        }
        // Refresh statistics
        fetchStats();
      } else {
        setError(response.error || 'Failed to delete note');
      }
    } catch (error) {
      setError('Failed to delete note');
    }
  };

  // Pagination handling
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchNotes(newPage);
  };

  // Initial load and refresh trigger
  useEffect(() => {
    if (userKeys && serverPublicKey) {
      fetchNotes(currentPage);
      fetchStats();
    }
  }, [userKeys, serverPublicKey, refreshTrigger, currentPage]);


  return (
    <div className="note-list">
      <div className="list-header">
        <div className="header-info">
          <h3>My Notes</h3>
          {stats && (
            <div className="stats">
              <span>{stats.total_notes} notes</span>
              <span>{formatFileSize(stats.total_size)}</span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="loading">
          Loading...
        </div>
      ) : notes.length === 0 ? (
        <div className="empty-state">
          <p>No notes yet</p>
          <p>Click "New Note" to create your first note</p>
        </div>
      ) : (
        <>
          <div className="notes-grid">
            {notes.map((note) => (
              <NoteItem
                key={note.note_id}
                note={note}
                onSelect={onNoteSelect}
                onEdit={onNoteEdit}
                onDelete={handleDeleteNote}
                isEditing={editingNoteId === note.note_id}
              />
            ))}
          </div>

          {pagination.total_pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="page-btn"
              >
                Previous
              </button>
              
              <span className="page-info">
                Page {currentPage} of {pagination.total_pages}
              </span>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= pagination.total_pages}
                className="page-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NoteList;
